import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import FlexBetween from "shared/FlexBetween";
import Header from "shared/Header";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import {
  Box,
  Button,
  useTheme,
  Tab,
  Tabs,
  Typography,
  Avatar,
  IconButton,
  InputBase,
  Collapse,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { indigo } from "@mui/material/colors";
import ConfirmationDialog from "shared/ConfirmationDialog";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import CreateGroups from "../create-groups/CreateGroups";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { getCalendarPdfUrl, dashboardStyle, tabStyles, pageContainerStyle, tableContainerStyle } from "shared/utils";
import Spinner from "shared/spinner/Spinner";
import useAWSDetails from "shared/useAWSDetails";

function EmployeeTable() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [switchConfirmationOpen, setSwitchConfirmationOpen] = useState(false);
  const [switchConfirmationData, setSwitchConfirmationData] = useState({
    userId: null,
    checked: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { fetchedBucketName, fetchedRegion } = useAWSDetails();

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL)
      .then((result) => setUsers(result.data))
      .catch((err) => console.log(err));
  }, []);

  const lightTheme = createTheme({
    palette: {
      primary: indigo,
    },
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleSwitchChange = (id, checked) => {
    const updatedUsers = users.map((user) => {
      if (user._id === id) {
        user.active = checked;
        const message = checked ? "User activated" : "User deactivated";
        checked ? toast.success(message) : toast.error(message);
      }
      return user;
    });

    setUsers(updatedUsers);

    axios
      .put(`${process.env.REACT_APP_API_URL}updateUser/${id}`, {
        active: checked,
      })
      .then((result) => {
        console.log(result);
        setSwitchConfirmationOpen(false);
      })
      .catch((err) => console.log(err));
  };

  const handleSwitchConfirmation = (userId, checked) => {
    setSwitchConfirmationData({ userId, checked });
    setSwitchConfirmationOpen(true);
  };

  const handleDeleteConfirmation = (id) => {
    setSelectedUserId(id);
    setConfirmationOpen(true);
  };

  const handleDelete = (id) => {
    setIsLoading(true);
    axios
      .delete(process.env.REACT_APP_API_URL + "deleteUser/" + id)
      .then((result) => {
        setUsers((prevUsers) => prevUsers.filter((user) => user._id !== id));
        setConfirmationOpen(false);
        toast.success("User deleted successfully");
        console.log(result);
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  };

  // Toggle search bar
  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchQuery("");
    }
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter rows based on search query
  const getFilteredRows = () => {
    const normalizeGroupName = (value) => {
      if (!value || value === "undefined" || value === "null") {
        return "Not assigned";
      }
      return value;
    };
    const allRows = users
      .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
      .map((user) => ({
        id: user._id,
        emp_id: user.employeeId,
        profilepic: user.profilepic,
        name: user.name,
        email: user.email,
        number: user.number || "N/A",
        role: user.role,
        workLocation: user.workLocation,
        groupname: normalizeGroupName(user.groupname),
        department: user.department,
        country: user.country,
        createdOn: user.createdOn,
        updatedOn: user.updatedOn,
        action: null,
        status: user.active,
      }));

    if (!searchQuery.trim()) {
      return allRows;
    }

    const query = searchQuery.toLowerCase().trim();
    return allRows.filter((row) => {
      return (
        row.emp_id?.toString().toLowerCase().includes(query) ||
        row.name?.toLowerCase().includes(query) ||
        row.email?.toLowerCase().includes(query) ||
        row.number?.toString().toLowerCase().includes(query) ||
        row.role?.toLowerCase().includes(query) ||
        row.workLocation?.toLowerCase().includes(query) ||
        row.groupname?.toLowerCase().includes(query) ||
        row.department?.toLowerCase().includes(query) ||
        row.country?.toLowerCase().includes(query)
      );
    });
  };

  const rows = getFilteredRows();

  const getRowClassName = (params) => {
    return params.row.status ? "" : "inactive-row";
  };

  const columns = [
    {
      field: "emp_id",
      headerName: "Emp Id",
      flex: 0.5,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Emp Id
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "Employee",
      flex: 2,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Employee
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            width: "100%",
            gap: "10px",
          }}
        >
          <Avatar
            src={`https://${fetchedBucketName}.s3.${fetchedRegion}.amazonaws.com/${getCalendarPdfUrl()}/employees/${
              params.row.emp_id
            }/profile-picture/${params.row.profilepic}`}
            sx={{ width: "32px", height: "32px", flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0, lineHeight: 1.25 }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "#222836",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.value}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#666",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "number",
      headerName: "Phone number",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Phone number
        </Typography>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Role
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              color: "#222836",
              margin: 0,
            }}
          >
            {params.value}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#666", margin: 0 }}>
            {params.row.department}
          </Typography>
        </Box>
      ),
    },
    {
      field: "workLocation",
      headerName: "Work Location",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Work Location
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              color: "#222836",
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            {`${params.value || "N/A"}, ${params.row.country || "N/A"}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "groupname",
      headerName: "Group Name",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Group Name
        </Typography>
      ),
    },
    {
      field: "updatedOn",
      headerName: "Updated On",
      flex: 0.8,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Updated On
        </Typography>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ width: "100%", textAlign: "center", fontSize: "14px", fontWeight: "bold" }}>
          Action
        </Typography>
      ),
      renderCell: (params) => {
        const isDisabled = !params.row.status;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
            }}
          >
            {isDisabled ? (
              <EditIcon
                style={{
                  marginRight: "10px",
                  color: "#cccccc",
                  fontSize: "25px",
                  cursor: "not-allowed",
                  opacity: 0.5,
                }}
              />
            ) : (
              <Link to={`/updateuser/${params.id}`}>
                <EditIcon
                  style={{
                    marginRight: "10px",
                    color: "#04497a",
                    fontSize: "25px",
                  }}
                />
              </Link>
            )}

            <DeleteIcon
              style={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                color: isDisabled ? "#cccccc" : "#04497a",
                fontSize: "25px",
                marginRight: "10px",
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={
                isDisabled ? undefined : () => handleDeleteConfirmation(params.id)
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={params.row.status}
                  onChange={() =>
                    handleSwitchConfirmation(params.id, !params.row.status)
                  }
                  sx={{
                    "& .MuiSwitch-thumb": {
                      color: "#04497a",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#222831",
                    },
                  }}
                />
              }
              sx={{ margin: 0 }}
            />
          </div>
        );
      },
    },
  ];

  const styles = tabStyles(theme, selectedTab);

  return (
    <div>
      {/* Use consistent page container style */}
      <Box sx={pageContainerStyle}>
        {/* Loading Overlay */}
        {isLoading && <Spinner />}
        
        {/* Tabs Section */}
        <Box>
          <ThemeProvider theme={lightTheme}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                marginBottom: "20px",
                height: "60px",
                color: "white",
                borderBottom: "1px solid #e5e5e5",
              }}
            >
              <Tab
                label="Manage Employees"
                icon={<ManageAccountsOutlinedIcon sx={{ marginRight: "4px" }} />}
                sx={styles}
              />
              <Tab
                label="Manage Groups"
                icon={<GroupsOutlinedIcon sx={{ marginRight: "4px" }} />}
                sx={styles}
              />
            </Tabs>
          </ThemeProvider>
        </Box>

        {selectedTab === 0 && (
          /* Use flex container that takes remaining space */
          <Box sx={tableContainerStyle}>
            {/* Header Section */}
            <FlexBetween sx={{ marginBottom: "15px" }}>
              <Header
                title="Manage Employees"
                subtitle="Add, Delete or Manage Employee Accounts"
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {/* Animated Search Bar */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: isSearchOpen ? "#f5f5f5" : "transparent",
                    borderRadius: "25px",
                    border: isSearchOpen ? "1px solid #ddd" : "none",
                    transition: "all 0.3s ease-in-out",
                    overflow: "hidden",
                  }}
                >
                  <Collapse in={isSearchOpen} orientation="horizontal" timeout={300}>
<InputBase
  placeholder="Search by name, role, location..."
  value={searchQuery}
  onChange={handleSearchChange}
  inputProps={{
    style: {
      color: "#333",
    },
  }}
  sx={{
    ml: 2,
    width: isSearchOpen ? "280px" : "0px",
    transition: "width 0.3s ease-in-out",
    color: "#333 !important",
    "& .MuiInputBase-input": {
      color: "#333 !important",
      padding: "8px 0",
    },
    "& input::placeholder": {
      color: "#888 !important",
      opacity: 1,
    },
  }}
  autoFocus={isSearchOpen}
/>
                  </Collapse>
                  <IconButton
                    onClick={handleSearchToggle}
                    sx={{
                      backgroundColor: isSearchOpen ? "transparent" : "#04497a",
                      color: isSearchOpen ? "#04497a" : "#ffffff",
                      "&:hover": {
                        backgroundColor: isSearchOpen ? "#eee" : "#033d68",
                        color: isSearchOpen ? "#04497a" : "#ffffff",
                      },
                      transition: "all 0.3s ease-in-out",
                    }}
                  >
                    {isSearchOpen ? <CloseIcon /> : <SearchIcon />}
                  </IconButton>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => navigate("/createuser")}
                  sx={{
                    backgroundColor: theme.palette.secondary.light,
                    color: theme.palette.background.alt,
                    fontSize: "14px",
                    fontWeight: "bolder",
                    padding: "10px 20px",
                  }}
                >
                  <PersonAddAltIcon sx={{ mr: "10px" }} />
                  Add Employee
                </Button>
              </Box>
            </FlexBetween>

            {/* Search Results Info */}
            {searchQuery && (
              <Typography sx={{ mb: 1, color: "#666", fontSize: "14px" }}>
                {rows.length} result{rows.length !== 1 ? "s" : ""} found for "{searchQuery}"
              </Typography>
            )}

            {/* Table Container - Takes all remaining space */}
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <ThemeProvider theme={lightTheme}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  pageSizeOptions={[5, 10]}
                  rowHeight={68}
                  headerHeight={48}
                  disableRowSelectionOnClick
                  getRowClassName={getRowClassName}
                  sx={{
                    ...dashboardStyle(theme),
                    "& .inactive-row": {
                      opacity: 0.45,
                      bgcolor: "rgba(0, 0, 0, 0.08)",
                    },
                  }}
                />
              </ThemeProvider>
            </Box>

            <ToastContainer />
            <ConfirmationDialog
              open={confirmationOpen}
              handleClose={() => setConfirmationOpen(false)}
              handleConfirm={() => handleDelete(selectedUserId)}
              message="Are you sure you want to delete this user?"
            />
            <ConfirmationDialog
              open={switchConfirmationOpen}
              handleClose={() => setSwitchConfirmationOpen(false)}
              handleConfirm={() =>
                handleSwitchChange(
                  switchConfirmationData.userId,
                  switchConfirmationData.checked
                )
              }
              message={`Are you sure you want to ${
                switchConfirmationData.checked ? "activate" : "deactivate"
              } this user?`}
            />
          </Box>
        )}

        {selectedTab === 1 && (
          <Box sx={tableContainerStyle}>
            <CreateGroups />
          </Box>
        )}
      </Box>
    </div>
  );
}

export default EmployeeTable;